import EditTimerDialog from "@/components/EditTimer/editTimerDialog.component";
import ErrorBoundary from "@/components/ErrorBoundary/errorBoundary.component";
import TimerCellAssignee from "@/components/SingleLineTimer/SingleLineTimerCells/timerCellAssignee.component";
import TimerCellDateTime from "@/components/SingleLineTimer/SingleLineTimerCells/timerCellDateTime.component";
import TimerCellTitle from "@/components/SingleLineTimer/SingleLineTimerCells/timerCellTitle.component";
import SingleTag from "@/components/Tags/tag.component";
import { ROUTE_TASKS } from "@/constants/routes.constants";
import useHandleSave from "@/hooks/SingleLineTimers/useHandleSave.hook";
import useOutsideClick from "@/hooks/SingleLineTimers/useOutsideClick.hook";
import useSaveAllowed from "@/hooks/SingleLineTimers/useSaveAllowed.hook";
import { sendDeleteUserTimerEvent } from "@/models/timers.model";
import { AppContext } from "@/services/AppContext/appContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shadcn/components/ui/alert-dialog";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/shadcn/components/ui/context-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip";
import { OrganizedTimersEvent } from "@/types";
import {
  calculateTimeDifference,
  formatElapsedTime,
  genericFailureToast,
  genericSuccessToast,
  getNewTaskTagByID,
} from "@/utils/utils";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

type SingleLineTimerProps = {
  timer: OrganizedTimersEvent;
  type: "older" | "Today" | "This Week" | "No Events";
};

const SingleLineTimer: React.FC<SingleLineTimerProps> = ({ timer, type }) => {
  const {
    userTags,
    fetchPageTimers,
    fetchEveryoneTimers,
    runningTimer,
    initialGetUserTimers,
    user,
  } = useContext(AppContext);

  const { task_name, name, id, tags, user_id, task_id } = timer.timer;
  const { event_end, event_start, position } = timer;

  const nav = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const [clickTimeout, setClickTimeout] = useState<NodeJS.Timeout | null>(null);
  const [editedTimerEvent, setEditedTimerEvent] =
    useState<OrganizedTimersEvent | null>(null);

  const createAssigneeOpen = useRef<boolean>(false);
  const createDateTimeStartOpen = useRef<boolean>(false);
  const createDateTimeEndOpen = useRef<boolean>(false);

  const controllerRef = React.useRef(new AbortController());

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const saveAllowed = useRef<boolean>(false);

  const editedTimerRef = useRef(editedTimerEvent);
  useEffect(() => {
    console.log("editedTimerEvent", editedTimerEvent);
    editedTimerRef.current = editedTimerEvent;
  }, [editedTimerEvent]);

  const handleOpenEditDialog = () => setIsEditDialogOpen(true);
  const handleCloseEditDialog = () => setIsEditDialogOpen(false);

  const handleOpenDeleteDialog = () => setIsDeleteDialogOpen(true);
  const handleCloseDeleteDialog = () => setIsDeleteDialogOpen(false);

  // Delete Timer
  const deleteTimer = async () => {
    // TODO: could probably stop then delete, would require more changes to the context/provider
    if (runningTimer?.id === id) {
      genericFailureToast("That timer is still running!");
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    const signal = controller.signal;

    const response = await sendDeleteUserTimerEvent({
      timerID: id,
      eventPosition: position,
      eventStartTime: event_start,
    });
    if (response.rStatus === "success") {
      genericSuccessToast("Timer Deleted");
      fetchPageTimers(signal, fetchEveryoneTimers);
      initialGetUserTimers(user.id, true);
      console.log("Timer Deleted");
    } else {
      genericFailureToast(`Failed to delete timer, ${response.message}`);
    }
  };

  const handleSingleClick = () => {
    if (isEditing) {
      return;
    }
    if (clickTimeout) {
      clearTimeout(clickTimeout);
    }

    setClickTimeout(
      setTimeout(() => {
        if (rowRef.current) {
          rowRef.current.style.outline = "1px solid #cccccc50";
          setIsEditing(true);
          setEditedTimerEvent(timer);
        }
      }, 0)
    );
  };

  // Sends updated timer to the server
  const { handleSave } = useHandleSave(
    editedTimerEvent,
    saveAllowed,
    editedTimerRef,
    setIsEditing,
    setEditedTimerEvent
  );

  // Handle clicks outside the row
  useOutsideClick(
    rowRef,
    createAssigneeOpen,
    createDateTimeStartOpen,
    createDateTimeEndOpen,
    saveAllowed,
    editedTimerRef,
    handleSave,
    setIsEditing
  );

  function handleFieldTitle(value: string) {
    if (!editedTimerEvent) return;

    setEditedTimerEvent((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        timer: { ...prev.timer, name: value },
        position: prev.position,
      };
    });
  }

  function handleFieldAssignedTo(value: number) {
    if (!editedTimerEvent) return;

    setEditedTimerEvent((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        timer: { ...prev.timer, user_id: value },
        position: prev.position,
      };
    });
  }

  useEffect(() => {
    if (editedTimerEvent) {
      console.log(editedTimerEvent);
    }
  }, [editedTimerEvent]);

  function handleFieldStart(value: string) {
    if (!editedTimerEvent) return;

    // Cancel if end is before start
    if (editedTimerEvent.event_end) {
      const startTime = new Date(value).getTime();
      const endTime = new Date(editedTimerEvent.event_end).getTime();
      if (startTime >= endTime) {
        genericFailureToast("Start time must be before end time");
        return;
      }
    }

    setEditedTimerEvent((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        event_start: value,
      };
    });
  }

  function handleFieldEnd(value: string | null) {
    if (!editedTimerEvent) return;

    // Cancel if end is before start
    if (value) {
      const startTime = new Date(editedTimerEvent.event_start).getTime();
      const endTime = new Date(value).getTime();
      if (startTime >= endTime) {
        genericFailureToast("End time must be after start time");
        return;
      }
    }

    setEditedTimerEvent((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        event_end: value ? value : undefined,
      };
    });
  }

  useSaveAllowed(editedTimerEvent, timer, saveAllowed);

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger>
          <div
            className={`grid grid-cols-12 items-center py-1 hover:bg-accent hover:text-accent-foreground border-l-4 h-12 select-none
            ${
              type === "Today"
                ? "border-green-300"
                : type === "This Week"
                  ? "border-indigo-300"
                  : type === "older"
                    ? "border-gray-300"
                    : type === "No Events"
                      ? "border-red-300"
                      : ""
            }
                
            `}
            ref={rowRef}
            onClick={handleSingleClick}
          >
            <ErrorBoundary
              fallback={
                <div className="text-center font-bold text-red-600 text-sm">
                  Error with TimerCellTitle
                </div>
              }
            >
              <TimerCellTitle
                className="px-2 col-span-2"
                editing={isEditing}
                editedTitle={editedTimerEvent?.timer.name}
                title={
                  editedTimerEvent
                    ? editedTimerEvent.timer.name
                    : name
                      ? name
                      : ""
                }
                inputID="edit-title"
                handleEditField={handleFieldTitle}
              />
            </ErrorBoundary>
            <ErrorBoundary
              fallback={
                <div className="text-center font-bold text-red-600 text-sm">
                  Error with TimerCellTitle
                </div>
              }
            >
              {task_name ? (
                <TooltipProvider>
                  <Tooltip delayDuration={400}>
                    <TooltipTrigger asChild>
                      <div
                        className="px-2 col-span-2 w-full truncate"
                        onClick={
                          isEditing
                            ? () => {
                                nav(`${ROUTE_TASKS}/${task_id}`);
                              }
                            : undefined
                        }
                      >
                        {task_name}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      className="bg-accent outline outline-1 outline-white dark:outline-neutral-700/35 text-accent-foreground text-[15px]"
                      align="start"
                      onClick={() => {
                        nav(`${ROUTE_TASKS}/${task_id}`);
                      }}
                    >
                      {task_name}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <div className="px-2 col-span-2 w-full truncate">No Task</div>
              )}
            </ErrorBoundary>
            <ErrorBoundary
              fallback={
                <div className="text-center font-bold text-red-600 text-sm">
                  Error with TimerCellAssignee
                </div>
              }
            >
              {/* Assignee */}
              <TimerCellAssignee
                className="px-2 col-span-2"
                assignedTo={user_id}
                editedAssignedTo={editedTimerEvent?.timer.user_id}
                editing={isEditing}
                handleFieldAssignedTo={handleFieldAssignedTo}
                isOpen={createAssigneeOpen}
              />
            </ErrorBoundary>

            <ErrorBoundary
              fallback={
                <div className="text-center font-bold text-red-600 text-sm">
                  Error with TimerCellDateTime
                </div>
              }
            >
              <TimerCellDateTime
                time={event_start}
                className="px-2 col-span-2"
                editedTime={editedTimerEvent?.event_start}
                editing={isEditing}
                handleEditField={handleFieldStart}
                isOpen={createDateTimeStartOpen}
              />
            </ErrorBoundary>
            <ErrorBoundary
              fallback={
                <div className="text-center font-bold text-red-600 text-sm">
                  Error with TimerCellDateTime
                </div>
              }
            >
              <TimerCellDateTime
                time={event_end || ""}
                className="px-2 col-span-2"
                editedTime={editedTimerEvent?.event_end || undefined}
                editing={isEditing}
                handleEditField={handleFieldEnd}
                isOpen={createDateTimeEndOpen}
              />
            </ErrorBoundary>
            <ErrorBoundary
              fallback={
                <div className="text-center font-bold text-red-600 text-sm">
                  Error with Tags
                </div>
              }
            >
              {/* Tags */}
              <div className="px-2 col-span-1 w-full">
                {tags && tags.length > 0 && (
                  <TooltipProvider>
                    <Tooltip delayDuration={150}>
                      <TooltipTrigger>
                        <div className="flex flex-row">
                          {tags.slice(0, 1).map((tag) => {
                            const fullTag = getNewTaskTagByID(tag, userTags);
                            return (
                              fullTag && (
                                <SingleTag
                                  key={fullTag.tag + fullTag.category_id}
                                  id={fullTag.tag + fullTag.category_id}
                                  color={fullTag.color}
                                  name={fullTag.tag}
                                  full={false}
                                />
                              )
                            );
                          })}
                          {tags.length > 1 && (
                            <span className="ms-1 truncate max-w-full">
                              ...
                            </span>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        className="bg-accent dark:bg-accent outline outline-1 outline-white dark:outline-1 dark:outline-neutral-700/35"
                        align="start"
                      >
                        {tags.map((tag) => {
                          const fullTag = getNewTaskTagByID(tag, userTags);
                          return (
                            fullTag && (
                              <div
                                key={fullTag.tag + fullTag.category_id}
                                className="pb-1"
                              >
                                <SingleTag
                                  id={fullTag.tag + fullTag.category_id}
                                  color={fullTag.color}
                                  name={fullTag.tag}
                                  full={true}
                                />
                              </div>
                            )
                          );
                        })}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </ErrorBoundary>
            <div className="px-2 col-span-1 w-full text-right">
              {event_start && event_end
                ? formatElapsedTime(
                    calculateTimeDifference(event_start, event_end)
                  )
                : "No Events"}
            </div>
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="outline outline-1 outline-gray-300 dark:outline-gray-700">
          {/* <ContextMenuItem
            className="text-left w-full block"
            onSelect={handleOpenEditDialog}
          >
            Edit
          </ContextMenuItem> */}
          <ContextMenuItem
            onSelect={handleOpenDeleteDialog}
            className="text-left w-full block text-red-500"
          >
            Delete
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={handleCloseDeleteDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete timer event?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              timer event.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                await deleteTimer();
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Editing */}
      {isEditDialogOpen && (
        <EditTimerDialog
          timerToEdit={timer}
          isOpen={isEditDialogOpen}
          onClose={handleCloseEditDialog}
        />
      )}
    </>
  );
};

export default SingleLineTimer;
